'use client';

import React, { useState, useEffect } from 'react';
import { Disclosure } from '@headlessui/react';
import { MinusIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import { billingApi, Product, Subscription, UsageData } from '@/app/api/billing';
import UsageStat from '@/components/billing/UsageStats';
import { Invoice } from '@/app/api/billing';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { tenantLLMApi, LLMConfig } from '@/services/tenant-llm-api';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function BillingPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [llmConfig, setLlmConfig] = useState<LLMConfig | null>(null);
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [planChangeInfo, setPlanChangeInfo] = useState<{ newPriceId: string; newPlanName: string; isUpgrade: boolean; upgradeType?: 'immediate' | 'scheduled' } | null>(null);
  const [upgradeConfirmText, setUpgradeConfirmText] = useState('');
  const [isPlanButtonLoading, setPlanButtonLoading] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' && params.get('session_id')) {
      const sessionId = params.get('session_id')!;
      // Clear the URL params so this doesn't run on every refresh
      window.history.replaceState({}, document.title, window.location.pathname);

      const confirm = async () => {
        try {
          addToast('Confirming your subscription...', 'success');
          await billingApi.confirmCheckout(sessionId);
          addToast('Your subscription has been updated!', 'success');
          // Refetch data to update the UI
          const subData = await billingApi.getSubscription();
          setSubscription(subData);
        } catch (error) {
          console.error('Failed to confirm checkout:', error);
          addToast('Could not confirm your subscription. Please refresh the page.', 'error');
        }
      };
      confirm();
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [productsData, subscriptionData, usageData, invoicesData, llmConfigData] = await Promise.all([
          billingApi.getProducts(),
          billingApi.getSubscription(),
          billingApi.getUsage(),
          billingApi.getInvoices(),
          tenantLLMApi.getConfig().catch(() => null), // Don't fail if LLM config not available
        ]);
        setProducts(productsData);
        setSubscription(subscriptionData);
        setUsage(usageData);
        setInvoices(invoicesData);
        setLlmConfig(llmConfigData);
      } catch (error) {
        console.error('Failed to fetch billing data:', error);
        addToast('Could not load billing information. Please try again later.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user, addToast]);

  const handleStartSubscription = async (priceId: string) => {
    setPlanButtonLoading(priceId);
    try {
      // Backend will automatically apply BYOK discount if user has BYOK enabled
      // This is done server-side for security - client cannot be trusted
      const { url } = await billingApi.createCheckoutSession(priceId);
      if (url) {
        window.location.href = url;
      } else {
        addToast('Could not start the subscription process. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      addToast('An error occurred. Please try again later.', 'error');
    } finally {
      setPlanButtonLoading(null);
    }
  };

  const handleChoosePlan = (priceId: string, newPlanName: string) => {
    if (!subscription || !products) return;

    const currentPrice = products.flatMap(p => p.prices).find(p => p.id === subscription.price_id);
    const newPrice = products.flatMap(p => p.prices).find(p => p.id === priceId);

    if (!currentPrice || !newPrice) {
      addToast('Could not compare plans.', 'error');
      return;
    }

    const isUpgrade = newPrice.unit_amount > currentPrice.unit_amount;
    setPlanChangeInfo({ newPriceId: priceId, newPlanName, isUpgrade });
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeInfo || (planChangeInfo.isUpgrade && !planChangeInfo.upgradeType)) return;

    const endsSoon = subscription?.current_period_end && (subscription.current_period_end - Date.now() / 1000) < 7 * 24 * 60 * 60;
    if (planChangeInfo.isUpgrade && planChangeInfo.upgradeType === 'immediate' && endsSoon && upgradeConfirmText !== 'UPGRADE') {
      addToast('Please type UPGRADE to confirm the immediate upgrade.', 'error');
      return;
    }

    try {
      const { newPriceId, isUpgrade, upgradeType } = planChangeInfo;
      const applyImmediately = isUpgrade ? upgradeType === 'immediate' : true;
      const result = await billingApi.changePlan(newPriceId, applyImmediately);

      if (result.scheduled) {
        addToast(`Your plan will be changed at the end of your current billing cycle.`, 'success');
      } else {
        addToast(`Successfully ${isUpgrade ? 'upgraded' : 'changed'} your plan!`, 'success');
      }

      await refreshSubscription();

    } catch (error) {
      console.error('Failed to change plan:', error);
      addToast('Could not change your plan. Please try again.', 'error');
    } finally {
      setPlanChangeInfo(null);
      setUpgradeConfirmText('');
    }
  };

  const refreshSubscription = async () => {
    try {
      const subscriptionData = await billingApi.getSubscription();
      setSubscription(subscriptionData);
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
      addToast('Could not refresh subscription details.', 'error');
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      await billingApi.cancelDowngrade();
      addToast('Your scheduled downgrade has been canceled.', 'success');
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to cancel downgrade:', error);
      addToast('Could not cancel the scheduled downgrade. Please try again.', 'error');
    }
  };

  const handleResumePlan = async () => {
    try {
      await billingApi.resumePlan();
      addToast('Your subscription will continue past the current period.', 'success');
      await refreshSubscription();
    } catch (error) {
      console.error('Failed to resume subscription:', error);
      addToast('Could not resume your subscription. Please try again.', 'error');
    }
  };

  const openCancelModal = () => {
    setCancelConfirmText('');
    setShowCancelModal(true);
  };
  const closeCancelModal = () => setShowCancelModal(false);
  const confirmCancel = async () => {
    if (cancelConfirmText.trim().toUpperCase() !== 'CANCEL') {
      addToast('Please type CANCEL to confirm.', 'error');
      return;
    }
    setShowCancelModal(false);
    // keep backend confirmation dialog as a second safety? replace to direct cancel call
    try {
      await billingApi.cancelPlan();
      addToast('Your subscription has been scheduled for cancellation.', 'success');
      const subscriptionData = await billingApi.getSubscription();
      setSubscription(subscriptionData);
    } catch (error) {
      console.error('Failed to cancel plan:', error);
      addToast('Could not cancel your subscription. Please try again.', 'error');
    }
  };

  const daysLeft = (() => {
    if (!subscription?.trial_end) return null;
    const now = Math.floor(Date.now() / 1000);
    const secs = Math.max(0, subscription.trial_end - now);
    return Math.ceil(secs / (24 * 60 * 60));
  })();

  const pendingToProductName = (() => {
    if (!subscription?.pending_change) return null;
    const pid = subscription.pending_change.to_internal_product_id;
    const prod = products.find(p => p.id === pid);
    return prod?.name || null;
  })();

  const isTrial = !subscription || subscription.subscription_status === 'trialing';

  const sortedProducts = (() => {
    // Ensure Starter, Professional, Team order
    const order = ['starter', 'professional', 'team'];
    return [...products].sort((a, b) => {
      const ai = order.indexOf(a.name.toLowerCase());
      const bi = order.indexOf(b.name.toLowerCase());
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  })();

  const tierFeatures = (tierName: string) => {
    const name = tierName.toLowerCase();
    if (name === 'starter') return [
      'Up to 14 articles/month',
      'Up to 14 AI image generations/month (No free trial)',
      'Up to 320 AI edits/month',
      'No credit card required to start',
      'Standard AI features',
      'Email support',
    ];
    if (name === 'professional') return [
      'Up to 60 articles/month',
      'Up to 60 AI image generations/month',
      'Up to 1,500 AI edits/month',
      'Advanced AI capabilities',
      'Priority support',
      'SEO analysis tools',
    ];
    return [
      'Unlimited articles',
      'Unlimited AI image generations',
      'Unlimited AI edits',
      'Custom user seats',
      'Dedicated support & onboarding',
      'Advanced security features',
    ];
  };

  const TierSkeleton = () => (
    <div className="ring-1 ring-gray-200 flex flex-col justify-between rounded-3xl bg-white p-8 xl:p-10 animate-pulse">
      <div>
        <div className="h-7 w-32 bg-gray-200 rounded mb-4"></div>
        <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
        <div className="h-4 w-2/3 bg-gray-200 rounded mb-6"></div>
        <div className="flex items-baseline gap-x-1">
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
          <div className="h-6 w-12 bg-gray-200 rounded"></div>
        </div>
        <div className="mt-6 space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-x-3">
              <div className="h-6 w-5 bg-gray-200 rounded"></div>
              <div className="h-6 w-full bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 h-10 w-full bg-gray-200 rounded"></div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <div className="h-10 w-64 bg-gray-200 rounded mx-auto mb-6 animate-pulse"></div>
            <div className="h-6 w-96 bg-gray-200 rounded mx-auto mb-8 animate-pulse"></div>

            <div className="isolate mx-auto mt-8 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              <TierSkeleton />
              <TierSkeleton />
              <TierSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Your Current Plan
          </p>
          <div className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600 flex flex-col items-center gap-3">
            {isTrial ? (
              <p>You are currently on a <strong>7-day free trial</strong>.</p>
            ) : (
              <>
                <p>You are currently on the <strong>{subscription?.product_name}</strong> plan.</p>
                {llmConfig?.isTenantOwned && (
                  <div className="inline-flex items-center rounded-md bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                    🔑 BYOK Active - {subscription?.product_name?.toLowerCase() === 'starter' ? '50% Discount Applied' : 'Enhanced Privacy'}
                  </div>
                )}
              </>
            )}
            {!isTrial && subscription?.current_period_start && subscription?.current_period_end && (
              <div className="mt-4 rounded-md bg-blue-50 p-4 text-left">
                <p className="text-sm text-blue-800">
                  Your current billing cycle is from
                  <strong> {new Date(subscription.current_period_start * 1000).toLocaleDateString()} </strong>
                  to
                  <strong> {new Date(subscription.current_period_end * 1000).toLocaleDateString()}</strong>.
                </p>
              </div>
            )}

            {!isTrial && !subscription?.cancel_at_period_end && (
              <button onClick={openCancelModal}
                className="rounded-md bg-red-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500">
                Cancel Subscription
              </button>
            )}
          </div>

          {/* Cancellation banner */}
          {subscription?.cancel_at_period_end && (
            <div className="mt-4 rounded-md bg-red-50 p-4 text-left">
              <p className="text-sm text-red-800">
                Your subscription will end on{' '}
                {subscription?.current_period_end ? new Date(subscription.current_period_end * 1000).toLocaleDateString() : 'the end of this billing period'}.
                <button onClick={handleResumePlan}
                  className="ml-2 text-sm font-semibold text-blue-700 hover:text-blue-600">Resume
                  Subscription</button>
              </p>
            </div>
          )}

          {/* Pending change banner */}
          {subscription?.pending_change && (
            <div className="mt-4 rounded-md bg-yellow-50 p-4 text-left">
              <p className="text-sm text-yellow-800">
                Your plan will be automatically downgraded
                to <strong>{pendingToProductName || 'your new plan'}</strong> on{' '}
                {subscription.pending_change.apply_at ? new Date(subscription.pending_change.apply_at * 1000).toLocaleDateString() : 'the start of your next billing cycle'}.
                <button onClick={handleCancelDowngrade}
                  className="ml-2 text-sm font-semibold text-blue-600 hover:text-blue-500">Cancel
                  Downgrade</button>
              </p>
            </div>
          )}

          {usage && (
            <div className="mt-8 space-y-4 text-left">
              <UsageStat name="Articles (this month)" used={usage.usage.articles} limit={usage.quotas.articles} />
              <UsageStat name="AI Edits (this month)" used={usage.usage.ai_edits} limit={usage.quotas.ai_edits} />

              {/* AI Image Generation - show stats if usage > 0 (for VIP trial users), otherwise show hint */}
              {(() => {
                const hasUsage = (usage.usage.ai_images || 0) > 0;

                return (
                  <>
                    {/* Show usage stats if user has generated images (VIP trial users) */}
                    {hasUsage && (
                      <UsageStat
                        name="AI Image Generations (this month)"
                        used={usage.usage.ai_images || 0}
                        limit={usage.quotas.ai_images || 0}
                      />
                    )}

                    {/* Show hint for all trial users (VIP or not) */}
                    {isTrial && (
                      <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                          <svg className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-blue-900">AI Image Generation (Premium Feature)</p>
                            <p className="mt-1 text-sm text-blue-700">
                              {hasUsage
                                ? 'You have VIP access during trial. Upgrade to continue using this feature after trial ends.'
                                : 'Not available during free trial. Upgrade to generate custom AI images for your articles.'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show stats for paid users without hint */}
                    {!isTrial && !hasUsage && (
                      <UsageStat
                        name="AI Image Generations (this month)"
                        used={0}
                        limit={usage.quotas.ai_images || 0}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Quota exceeded prompts */}
          {usage && (() => {
            const hitArticles = usage.quotas.articles > 0 && usage.usage.articles >= usage.quotas.articles;
            const hitAi = usage.quotas.ai_edits > 0 && usage.usage.ai_edits >= usage.quotas.ai_edits;
            const exceeded = hitArticles || hitAi;
            if (!exceeded) return null;

            const starterTier = products.find(p => p.name.toLowerCase() === 'starter');
            const proTier = products.find(p => p.name.toLowerCase() === 'professional');

            return (
              <div className="mt-6 rounded-md bg-orange-50 p-4 text-left">
                <p className="text-sm text-orange-800">
                  {isTrial
                    ? 'You have reached the trial limits. Subscribe to continue creating and editing content.'
                    : 'You have reached your current plan limits. Upgrade to continue creating and editing content.'}
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {isTrial && starterTier && (
                    <button
                      onClick={() => handleStartSubscription(starterTier.prices[0].id)}
                      className="rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                    >
                      Start as Starter
                    </button>
                  )}
                  {proTier && (
                    <button
                      onClick={() => handleStartSubscription(proTier.prices[0].id)}
                      className="rounded-md bg-gray-900 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                    >
                      {isTrial ? 'Start as Professional' : 'Upgrade to Professional'}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="mt-16">
            <h3 className="text-2xl font-bold tracking-tight text-gray-900">{isTrial ? 'Subscribe Now' : 'Change Your Plan'}</h3>
            <div
              className="isolate mx-auto mt-8 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {sortedProducts.map((tier, tierIdx) => (
                <div key={tier.id}
                  className={classNames('ring-1 ring-gray-200', 'flex flex-col justify-between rounded-3xl bg-white p-8 xl:p-10')}>
                  <div>
                    <h3 id={tier.id}
                      className={classNames(subscription?.product_id === tier.id ? 'text-blue-600' : 'text-gray-900', 'text-lg font-semibold leading-8')}>{tier.name}</h3>
                    <p className="mt-4 text-sm leading-6 text-gray-600">{tier.description}</p>
                    <p className="mt-6 flex items-baseline gap-x-1">
                      {(() => {
                        // Show BYOK price for Starter if user has BYOK enabled
                        const isStarter = tier.name.toLowerCase() === 'starter';
                        const hasByok = llmConfig?.isTenantOwned;
                        const byokPrice = tier.prices.find(p => p.id.includes('_byok'));

                        if (isStarter && hasByok && byokPrice) {
                          return (
                            <>
                              <span className="text-4xl font-bold tracking-tight text-green-600">${(byokPrice.unit_amount / 100).toFixed(2)}</span>
                              <span className="text-sm font-semibold leading-6 text-gray-600">/month</span>
                              <span className="ml-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                                BYOK 50% OFF
                              </span>
                            </>
                          );
                        }

                        return (
                          <>
                            <span className="text-4xl font-bold tracking-tight text-gray-900">${(tier.prices[0].unit_amount / 100).toFixed(2)}</span>
                            <span className="text-sm font-semibold leading-6 text-gray-600">/month</span>
                          </>
                        );
                      })()}
                    </p>
                    <ul role="list" className="mt-6 space-y-3 text-sm leading-6 text-gray-600 text-left">
                      {tierFeatures(tier.name).map((f) => (
                        <li key={f} className="flex gap-x-3"><CheckIcon
                          className="h-6 w-5 flex-none text-blue-600" />{f}</li>
                      ))}
                    </ul>
                  </div>
                  {tier.id === 'prod_team' ? (
                    <a href="mailto:your-sales-email@example.com?subject=Team Plan Inquiry"
                      className="mt-8 block rounded-md bg-gray-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700">Contact
                      Us</a>
                  ) : (
                    (() => {
                      const isCurrent = subscription?.product_id === tier.id;
                      if (isTrial) {
                        const isStarter = tier.name.toLowerCase() === 'starter';
                        return (
                          <button
                            onClick={() => handleStartSubscription(tier.prices[0].id)}
                            disabled={isPlanButtonLoading === tier.prices[0].id}
                            className={classNames(
                              isStarter ? 'bg-blue-600 hover:bg-blue-500' : 'bg-gray-900 hover:bg-gray-800',
                              'mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white',
                              isPlanButtonLoading === tier.prices[0].id ? 'opacity-50 cursor-not-allowed' : ''
                            )}
                          >
                            {isPlanButtonLoading === tier.prices[0].id ? 'Processing...' : `Start as ${tier.name}`}
                          </button>
                        );
                      }
                      if (isCurrent) {
                        return (
                          <button disabled
                            className={classNames('bg-gray-400', 'mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white')}>
                            Current Plan
                          </button>
                        );
                      }
                      // decide upgrade/downgrade by price amount
                      const currentTier = products.find(p => p.id === (subscription?.product_id || ''));
                      const currentAmount = currentTier ? (currentTier.prices[0].unit_amount || 0) : 0;
                      const targetAmount = tier.prices[0].unit_amount || 0;
                      const isUpgrade = targetAmount > currentAmount;
                      return (
                        <button onClick={() => handleChoosePlan(tier.prices[0].id, tier.name)}
                          disabled={isPlanButtonLoading === tier.prices[0].id}
                          className={classNames(
                            isUpgrade ? 'bg-blue-600 hover:bg-blue-500' : 'bg-gray-900 hover:bg-gray-800',
                            'mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white',
                            isPlanButtonLoading === tier.prices[0].id ? 'opacity-50 cursor-not-allowed' : ''
                          )}>

                          {isUpgrade ? 'Upgrade' : 'Downgrade'}
                        </button>
                      );
                    })()
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold tracking-tight text-gray-900">Billing History</h3>
            <ul role="list" className="divide-y divide-gray-100 mt-8">
              {invoices.map((invoice) => (
                <li key={invoice.id} className="flex justify-between gap-x-6 py-5">
                  <div className="flex min-w-0 gap-x-4">
                    <div className="min-w-0 flex-auto">
                      <p className="text-sm font-semibold leading-6 text-gray-900">Invoice
                        for {new Date(invoice.created * 1000).toLocaleDateString()}</p>
                      <p className="mt-1 truncate text-xs leading-5 text-gray-500">{invoice.status}</p>
                    </div>
                  </div>
                  <div className="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                    <p className="text-sm leading-6 text-gray-900">${(invoice.amount_paid / 100).toFixed(2)}</p>
                    <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer"
                      className="mt-1 text-xs leading-5 text-gray-500 hover:text-gray-900">Download PDF</a>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Removed Manage Subscription portal. Cancel is handled via confirmation modal above. */}
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h4 className="text-lg font-semibold text-gray-900">Confirm Cancellation</h4>
            <p className="mt-2 text-sm text-gray-600">
              This will schedule your subscription to cancel at the end of the current billing period. You will retain
              access until{' '}
              {subscription?.current_period_end ? new Date(subscription.current_period_end * 1000).toLocaleDateString() : 'the end of this billing period'}.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Type <span className="font-semibold">CANCEL</span> to confirm.
            </p>
            <input
              type="text"
              value={cancelConfirmText}
              onChange={(e) => setCancelConfirmText(e.target.value)}
              className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="CANCEL"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeCancelModal}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Back
              </button>
              <button onClick={confirmCancel}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500">Confirm
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {planChangeInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div
                  className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5"
                    stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <h3 className="text-base font-semibold leading-6 text-gray-900" id="modal-title">Confirm Plan
                    Change</h3>
                  <div className="mt-2">
                    {!planChangeInfo.isUpgrade ? (
                      <p className="text-sm text-gray-500">
                        Are you sure you want to downgrade to the <span
                          className="font-bold">{planChangeInfo.newPlanName}</span> plan? This change will take
                        effect at the end of your current billing cycle.
                      </p>
                    ) : !planChangeInfo.upgradeType ? (
                      <div>
                        <p className="text-sm text-gray-500 mb-4">How would you like to apply this upgrade?</p>
                        <div className="space-y-2">
                          <button onClick={() => setPlanChangeInfo({ ...planChangeInfo, upgradeType: 'immediate' })}
                            className="w-full text-left p-3 border rounded-md hover:bg-gray-50">
                            <p className="font-semibold">Upgrade Now</p>
                            <p className="text-xs text-gray-500">Get access to new features immediately. You'll be
                              charged the difference on your next invoice.</p>
                          </button>
                          <button onClick={() => setPlanChangeInfo({ ...planChangeInfo, upgradeType: 'scheduled' })}
                            className="w-full text-left p-3 border rounded-md hover:bg-gray-50">
                            <p className="font-semibold">Upgrade at Next Renewal</p>
                            <p className="text-xs text-gray-500">Your plan will be upgraded
                              on {subscription?.current_period_end ? new Date(subscription.current_period_end * 1000).toLocaleDateString() : 'your next renewal date'}.</p>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500">
                          You are upgrading to the <span
                            className="font-bold">{planChangeInfo.newPlanName}</span> plan.
                          {planChangeInfo.upgradeType === 'immediate' ? ' This will take effect immediately.' : ' This will take effect at your next renewal.'}
                        </p>
                        {planChangeInfo.upgradeType === 'immediate' && subscription?.current_period_end && (subscription.current_period_end - Date.now() / 1000) < 7 * 24 * 60 * 60 && (
                          <div className="mt-4">
                            <p className="text-sm text-yellow-800 bg-yellow-50 p-3 rounded-md">Your billing cycle
                              is ending soon. To confirm an immediate upgrade, please type <span
                                className="font-bold">UPGRADE</span> below.</p>
                            <input
                              type="text"
                              value={upgradeConfirmText}
                              onChange={(e) => setUpgradeConfirmText(e.target.value)}
                              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                              placeholder="UPGRADE"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button type="button" onClick={handleConfirmPlanChange}
                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto">Confirm
              </button>
              <button type="button" onClick={() => {
                setPlanChangeInfo(null);
                setUpgradeConfirmText('');
              }}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
